import { useTranslation } from 'react-i18next';
import { bangkokClockTime } from '@/lib/datetime';
import { useMinuteTick } from '@/hooks/useMinuteTick';

interface Props {
  /** The clock, injected (§6.8). Omitted in the app, where it ticks itself. */
  now?: Date;
}

/**
 * The header clock, pinned to Asia/Bangkok.
 *
 * A remote operator may be working from any zone, and every timestamp they are
 * about to act on — a deposit's arrival, a settlement window, an audit row — is
 * denominated in the platform's business day. Showing the machine's own clock
 * next to those would invite exactly the off-by-seven-hours reading the columns
 * are formatted to prevent, so this reads `Asia/Bangkok` and says so in its
 * label (CLAUDE.md §3.9: date-fns-tz, never `toLocale*`/`Intl`).
 */
export function BangkokClock({ now }: Props) {
  const { t } = useTranslation();
  const tick = useMinuteTick(now);

  return (
    <span
      data-testid="bangkok-clock"
      title={t('common.bangkokTime')}
      className="hidden items-center gap-1 whitespace-nowrap px-2 text-xs tabular-nums text-muted-foreground sm:flex"
    >
      <span className="font-medium text-foreground">{bangkokClockTime(tick)}</span>
      <span>{t('common.bangkokTimeShort')}</span>
    </span>
  );
}
